/*
 * Entity Kubernetes Enhanced Content
 *
 * 根据 powered-by 类型展示不同内容：
 * - knative: 状态栏 + Pod 表格 + Events 表格
 * - argocd/kubernetes: 状态栏 + Deployment 表格 + Pod 表格 + Events 表格 + 资源指标
 */

import React from 'react';
import { Box, Grid, Typography, CircularProgress, Button } from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useKubernetesClusterInfo } from '../hooks/useKubernetesClusterInfo';
import { useKubernetesWorkloads } from '../hooks/useKubernetesWorkloads';
import { useKnativeWorkloads } from '../hooks/useKnativeWorkloads';
import { KubernetesHeader } from './KubernetesHeader';
import { KubernetesDeploymentCard } from './KubernetesDeploymentCard';
import { KubernetesResourceMetricsCard } from './KubernetesResourceMetricsCard';
import { KubernetesPodsTable } from './KubernetesPodsTable';
import { KubernetesEventsTimeline } from './KubernetesEventsTimeline';
import { KUBERNETES_ANNOTATIONS } from '../types';

export interface EntityKubernetesEnhancedContentProps {
  refreshIntervalMs?: number;
}

const ConfigMissingPanel: React.FC<{ entityName: string }> = ({ entityName }) => (
  <Box p={3}>
    <Typography variant="h6" gutterBottom>Kubernetes 配置缺失</Typography>
    <Typography variant="body2" color="textSecondary">
      Entity "{entityName}" 缺少 Kubernetes 注解，请在 catalog-info.yaml 中添加：
    </Typography>
    <Box mt={1} p={2} style={{ background: '#f5f5f5', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.8rem' }}>
      <div>backstage.io/kubernetes-cluster-name: &lt;cluster-name&gt;</div>
      <div>backstage.io/kubernetes-namespace: &lt;namespace&gt;</div>
    </Box>
  </Box>
);

const LoadingPanel: React.FC = () => (
  <Box display="flex" alignItems="center" justifyContent="center" p={6} style={{ gap: 16 }}>
    <CircularProgress size={24} />
    <Typography variant="body2" color="textSecondary">加载中...</Typography>
  </Box>
);

const ErrorPanel: React.FC<{ error: Error; onRetry: () => void }> = ({ error, onRetry }) => (
  <Box p={3}>
    <Typography variant="subtitle1" color="error" gutterBottom>加载失败</Typography>
    <Typography variant="body2" color="textSecondary" gutterBottom>{error.message}</Typography>
    <Button variant="outlined" size="small" onClick={onRetry}>重试</Button>
  </Box>
);

/**
 * ArgoCD / 传统 Kubernetes Deployment 模式
 */
const KubernetesContent: React.FC<{
  clusterInfo: ReturnType<typeof useKubernetesClusterInfo>;
  refreshIntervalMs?: number;
}> = ({ clusterInfo }) => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const kubernetesId = annotations[KUBERNETES_ANNOTATIONS.ID] || entity.metadata.name;
  const labelSelector =
    annotations[KUBERNETES_ANNOTATIONS.LABEL_SELECTOR] ||
    `backstage.io/kubernetes-id=${kubernetesId}`;

  const { pods, deployments, events, overview, metrics, loading, error, refresh } =
    useKubernetesWorkloads(clusterInfo.name, clusterInfo.namespace, labelSelector);

  if (loading && pods.length === 0) return <LoadingPanel />;
  if (error && pods.length === 0) return <ErrorPanel error={error} onRetry={refresh} />;

  return (
    <Box p={2}>
      <KubernetesHeader
        clusterName={clusterInfo.name}
        namespace={clusterInfo.namespace}
        workloadName={kubernetesId}
        onRefresh={refresh}
        loading={loading}
        podOverview={{ running: overview.pods.running, total: overview.pods.total }}
      />

      <Grid container spacing={2}>
        {/* Deployment 表格 */}
        <Grid item xs={12}>
          <KubernetesDeploymentCard deployments={deployments} loading={loading} />
        </Grid>

        {/* Pod 表格 + 资源指标（并排） */}
        <Grid item xs={12} md={8}>
          <KubernetesPodsTable pods={pods} loading={loading} />
        </Grid>
        <Grid item xs={12} md={4}>
          <KubernetesResourceMetricsCard metrics={metrics} loading={loading} />
        </Grid>

        {/* Events */}
        <Grid item xs={12}>
          <KubernetesEventsTimeline events={events} loading={loading} />
        </Grid>
      </Grid>
    </Box>
  );
};

/**
 * Knative Serverless 模式
 * （Revision/Traffic 信息已在 Knative Serverless tab，这里只展示 Pod 和 Events）
 */
const KnativeContent: React.FC<{
  clusterInfo: ReturnType<typeof useKubernetesClusterInfo>;
  refreshIntervalMs?: number;
}> = ({ clusterInfo }) => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const knativeServiceName =
    clusterInfo.knativeServiceName ||
    annotations['knative.dev/service-name'] ||
    entity.metadata.name;

  const { pods, events, overview, service, loading, error, refresh } = useKnativeWorkloads(
    clusterInfo.name,
    clusterInfo.namespace,
    knativeServiceName,
  );


  if (loading && !service) return <LoadingPanel />;
  if (error && !service) return <ErrorPanel error={error} onRetry={refresh} />;

  const serviceReady = overview.service.ready;

  return (
    <Box p={2}>
      <KubernetesHeader
        clusterName={clusterInfo.name}
        namespace={clusterInfo.namespace}
        workloadName={knativeServiceName}
        onRefresh={refresh}
        loading={loading}
        podOverview={{
          running: overview.pods.running,
          total: overview.pods.total,
        }}
      />

      {/* Service 状态异常时展示提示（含原因） */}
      {!serviceReady && service && (() => {
        const readyCondition = service.conditions?.find(c => c.type === 'Ready');
        const reason = readyCondition?.reason || '';
        const message = readyCondition?.message || '';
        return (
          <Box
            mb={2}
            p={1.5}
            style={{
              background: '#fff3e0',
              border: '1px solid #ffcc80',
              borderRadius: 4,
            }}
          >
            <Typography variant="body2" style={{ color: '#e65100', fontWeight: 600 }}>
              ⚠️ Knative Service 不处于 Ready 状态
              {reason && <span style={{ fontWeight: 400, marginLeft: 8 }}>({reason})</span>}
            </Typography>
            {message && (
              <Typography variant="body2" style={{ color: '#bf360c', marginTop: 4, fontSize: '0.8rem' }}>
                {message}
              </Typography>
            )}
          </Box>
        );
      })()}

      <Grid container spacing={2}>
        {/* Pod 表格 */}
        <Grid item xs={12}>
          <KubernetesPodsTable pods={pods} loading={loading} />
        </Grid>

        {/* Events */}
        <Grid item xs={12}>
          <KubernetesEventsTimeline events={events} loading={loading} />
        </Grid>
      </Grid>
    </Box>
  );
};

/**
 * 主入口：根据 powered-by 注解自动切换
 */
export const EntityKubernetesEnhancedContent = ({
  refreshIntervalMs,
}: EntityKubernetesEnhancedContentProps): JSX.Element => {
  const { entity } = useEntity();
  const clusterInfo = useKubernetesClusterInfo();

  const annotations = entity.metadata.annotations || {};
  const hasClusterConfig = !!annotations[KUBERNETES_ANNOTATIONS.CLUSTER_NAME];

  if (!hasClusterConfig) {
    return <ConfigMissingPanel entityName={entity.metadata.name} />;
  }

  if (clusterInfo.poweredBy === 'knative') {
    return <KnativeContent clusterInfo={clusterInfo} refreshIntervalMs={refreshIntervalMs} />;
  }

  return <KubernetesContent clusterInfo={clusterInfo} refreshIntervalMs={refreshIntervalMs} />;
};
